// src/features/IssuerEvents/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  DatePickerField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

// Import dropdown options from filter.tsx
import { eventTypeOptions, timeOfTheDayOptions } from "./filter";

// Transform string-based enum options into numeric-string options
const timeOfTheDayNewOptions = timeOfTheDayOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));
const eventTypeNewOptions = eventTypeOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));

// Zod schema: all fields optional unless required
const FormSchema = z.object({
  event_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  time_of_the_day: z.coerce.number().optional().nullable(),
  event_type: z.coerce.number().optional().nullable(),
  remark: z.string().nullable().optional(),
  issuer_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  issuer_id: z.coerce.number().min(1, "Issuer is required"),
});

export function IssuerEventsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                IssuerEvents {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Date and Time of Day */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="event_date"
                    label={t(`${table_name}.columns.event_date`, {
                      ns: "tables",
                      defaultValue: "Event Date",
                    })}
                    control={form.control}
                  />
                  <SelectField
                    name="time_of_the_day"
                    label={t(`${table_name}.columns.time_of_the_day`, {
                      ns: "tables",
                      defaultValue: "Time of Day",
                    })}
                    options={timeOfTheDayNewOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
                {/* Row 2: Event Type and Issuer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="event_type"
                    label={t(`${table_name}.columns.event_type`, {
                      ns: "tables",
                      defaultValue: "Event Type",
                    })}
                    options={eventTypeNewOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <AutocompleteAsyncField
                    name="issuer_name"
                    label={t(`${table_name}.columns.issuer_name`, {
                      ns: "tables",
                      defaultValue: "Issuer",
                    })}
                    base_url="legacy-quantilia/issuers"
                    column_name="name"
                    id_column="issuer_id"
                    control={form.control}
                    isRequired
                  />
                </div>
                {/* Row 3: Remark */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="remark"
                    label={t(`${table_name}.columns.remark`, {
                      ns: "tables",
                      defaultValue: "Remark",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.remarks")}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["issuer_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
